import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { addForm as addUserForm, removeForm as removeUserForm } from './auth.slice'
import {
  removeForm as removeSubmissionsForm,
  removeSubmission as removeSubmissions,
  resetPayload as resetSubmissionPayload,
  setSubmissions
} from './submission.slice'
import { FormService } from '../services/form.service'
import { deleteThumbnail, uploadThumbnail } from '../services/aws.service'
import {
  FormSchema,
  NewFormPayloadSchema,
  NewFormSchema,
  SectionSchema,
  TemplateSchema
} from '../utils/types/form'
// import { forms } from '../data/form'

const newForm: NewFormSchema = {
  name: 'Untitled form',
  description: 'Add description...',
  sections: [] as SectionSchema[],
  isPublic: true,
  meta: {
    colour: 'default'
  }
}
// const newForm = forms[0]

const initialState: {
  isLoading: boolean
  data: FormSchema[]
  thumbnails: Record<string, string>
  form: FormSchema | null
  template: TemplateSchema | null
  templates: TemplateSchema[]
  payload: NewFormPayloadSchema
} = {
  isLoading: false,
  data: [],
  thumbnails: {},
  form: null,
  template: null,
  templates: [],
  payload: { form: newForm }
}

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    setThumbnails: (state, data) => {
      state.thumbnails = { ...state.thumbnails, ...data.payload }
    },
    setForm: (state, data) => {
      state.form = data.payload
    },
    setTemplate: (state, data) => {
      state.template = data.payload
    },
    setPayload: (state, data) => {
      state.payload = data.payload
    },
    addForm: (state, data) => {
      state.data = [data.payload, ...state.data]
    },
    addTemplate: (state, data) => {
      state.templates = [...state.templates, data.payload]
    },
    updateForm: (state, data) => {
      if (state.data) {
        const updateIndex = state.data.findIndex(({ _id }) => _id === data.payload._id)
        state.data[updateIndex] = data.payload
      }

      if (state.form && state.form._id === data.payload._id) {
        state.form = data.payload
      }
    },
    updateTemplate: (state, data) => {
      if (state.templates) {
        const updateIndex = state.templates.findIndex(({ _id }) => _id === data.payload._id)
        state.templates[updateIndex] = data.payload
      }

      if (state.template && state.template._id === data.payload._id) {
        state.template = data.payload
      }
    },
    removeForm: (state, data) => {
      const formIndex = state.data.findIndex(({ _id }) => _id === data.payload)

      if (state.data[formIndex]) {
        state.data.splice(formIndex, 1)
      }

      if (state.form && state.form._id === data.payload) {
        state.form = null
      }
    },
    removeTemplate: (state, data) => {
      const templateIndex = state.templates.findIndex(({ _id }) => _id === data.payload)

      if (state.templates[templateIndex]) {
        state.templates.splice(templateIndex, 1)
      }

      if (state.template && state.template._id === data.payload) {
        state.template = null
      }
    },
    addSubmission: (state, data) => {
      const { formId, submissionId } = data.payload

      const formIndex = state.data.findIndex(({ _id }) => _id === formId)

      if (state.data[formIndex]) {
        state.data[formIndex].submissions.unshift(submissionId)
      }

      if (state.form && state.form._id === formId) {
        state.form.submissions.unshift(submissionId)
      }
    },
    removeSubmission: (state, data) => {
      const { formId, submissionId } = data.payload

      const formIndex = state.data.findIndex(({ _id }) => _id === formId)

      if (state.data[formIndex]) {
        const submissionIndex = state.data[formIndex].submissions.findIndex(
          (id) => id === submissionId
        )

        state.data[formIndex].submissions.splice(submissionIndex, 1)
      }

      if (state.form && state.form._id === formId) {
        const submissionIndex = state.form.submissions.findIndex((id) => id === submissionId)

        state.form.submissions.splice(submissionIndex, 1)
      }
    },
    removeThumbnail: (state, data) => {
      delete state.thumbnails[data.payload]
    },
    resetPayload: (state) => {
      state.payload = { form: newForm }
    },
    resetState: (state) => {
      state.isLoading = false
      state.data = []
      state.thumbnails = {}
      state.form = null
      state.payload = { form: newForm }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchForms.fulfilled, (state, action) => {
      state.data = action.payload
    })
    builder.addCase(fetchTemplates.fulfilled, (state, action) => {
      state.templates = action.payload
    })
    builder.addCase(fetchForm.fulfilled, (state, action) => {
      state.form = action.payload
    })
  }
})

export const fetchForms = createAsyncThunk('forms/fetch', async () => {
  try {
    const { data } = await FormService.getAll()
    return data
  } catch (error) {
    return Promise.reject(error)
  }
})

export const fetchTemplates = createAsyncThunk('forms/fetch-templates', async () => {
  try {
    const { data } = await FormService.getAllTemplates()
    return data
  } catch (error) {
    return Promise.reject(error)
  }
})

export const fetchSubmissions = createAsyncThunk(
  'forms/fetch-submissions',
  async (formId: string, { dispatch }) => {
    try {
      const { data } = await FormService.getSubmissions(formId)
      if (data) {
        dispatch(setSubmissions({ [formId]: data }))
      } else {
        dispatch(setSubmissions({ [formId]: [] }))
      }
      return data
    } catch (error) {
      const errorStr = JSON.stringify(error)
      return Promise.reject(errorStr)
    }
  }
)

export const fetchForm = createAsyncThunk('forms/fetch-one', async (id: string) => {
  try {
    const { data } = await FormService.getOne(id)
    return data
  } catch (error: any) {
    const errorStr = JSON.stringify(error)
    return Promise.reject(errorStr)
  }
})

export const createForm = createAsyncThunk(
  'forms/create',
  async (payload: { data: NewFormPayloadSchema; thumbnailUrl: string }, { dispatch }) => {
    try {
      const { data, status } = await FormService.create(payload.data)
      if (status === 201) {
        if (payload.thumbnailUrl) {
          await uploadThumbnail(payload.thumbnailUrl, data._id)
        }

        // * SCHEMA: Add form document to state
        dispatch(formSlice.actions.addForm(data))

        // * SCHEMA: Add formId to user
        dispatch(addUserForm(data._id))

        // * Reset payload
        dispatch(formSlice.actions.resetPayload())
      }
      return { data, status }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const createTemplate = createAsyncThunk(
  'forms/create-template',
  async (payload: { data: NewFormPayloadSchema; thumbnailUrl: string }, { dispatch }) => {
    try {
      const { data, status } = await FormService.createTemplate(payload.data)
      if (status === 201) {
        if (payload.thumbnailUrl) {
          await uploadThumbnail(payload.thumbnailUrl, data._id)
        }

        // * SCHEMA: Add template document to state
        dispatch(formSlice.actions.addTemplate(data))

        // * Reset payload
        dispatch(formSlice.actions.resetPayload())
      }
      return { data, status }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const updateForm = createAsyncThunk(
  'forms/update',
  async (payload: { data: NewFormPayloadSchema; thumbnailUrl: string }, { dispatch }) => {
    try {
      const { data, status } = await FormService.update(payload.data)

      if (status === 200) {
        if (payload.thumbnailUrl) {
          await uploadThumbnail(payload.thumbnailUrl, data._id)
        }

        // * SCHEMA: Update form document in state
        dispatch(formSlice.actions.updateForm(data))

        // * Remove blob URL
        dispatch(formSlice.actions.removeThumbnail(data._id))

        // * Reset payload
        dispatch(formSlice.actions.resetPayload())

        // * Reset submission payload
        dispatch(resetSubmissionPayload())
      }
      return { data, status }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const updateTemplate = createAsyncThunk(
  'forms/update-template',
  async (payload: { data: NewFormPayloadSchema; thumbnailUrl: string }, { dispatch }) => {
    try {
      const { data, status } = await FormService.updateTemplate(payload.data)

      if (status === 200) {
        if (payload.thumbnailUrl) {
          await uploadThumbnail(payload.thumbnailUrl, data._id)
        }

        // * SCHEMA: Update template document in state
        dispatch(formSlice.actions.updateTemplate(data))

        // * Remove blob URL
        dispatch(formSlice.actions.removeThumbnail(data._id))

        // * Reset payload
        dispatch(formSlice.actions.resetPayload())
      }
      return { data, status }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const publishForm = createAsyncThunk(
  'forms/publish',
  async (payload: { id: string; isPublished: boolean }, { dispatch }) => {
    try {
      const { data, status } = await FormService.publish(payload)

      if (status === 200) {
        // * SCHEMA: Update form document in state
        dispatch(formSlice.actions.updateForm(data))
      }
    } catch (error: any) {
      return Promise.reject(error.response.data.message ?? error)
    }
  }
)

export const deleteForm = createAsyncThunk(
  'forms/delete',
  async (formId: string, { dispatch, getState }) => {
    try {
      const { status } = await FormService.deleteOne(formId)

      if (status === 200) {
        // * Delete thumbnail
        deleteThumbnail(formId)

        // * Cleanup submissions
        const formState = (getState as Function)().form
        const form = (formState.data as FormSchema[]).find(({ _id }) => _id === formId)

        if (form) {
          for (const submissionId of form.submissions) {
            // * SCHEMA: Remove submissionIds from form
            dispatch(formSlice.actions.removeSubmission({ formId, submissionId }))

            // * SCHEMA: Delete submission documents from state
            dispatch(removeSubmissions({ formId, submissionId }))
          }
        }

        // * SCHEMA: Delete submissions form key from state
        dispatch(removeSubmissionsForm(formId))

        // * SCHEMA: Remove formId from user
        dispatch(removeUserForm(formId))

        // * SCHEMA: Delete form document from state
        dispatch(formSlice.actions.removeForm(formId))
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const deleteTemplate = createAsyncThunk(
  'forms/delete-template',
  async (templateId: string, { dispatch }) => {
    try {
      const { status } = await FormService.deleteTemplate(templateId)

      if (status === 200) {
        // * Delete thumbnail
        deleteThumbnail(templateId)

        // * SCHEMA: Delete template document from state
        dispatch(formSlice.actions.removeTemplate(templateId))
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const { reducer: formReducer } = formSlice
export const {
  setThumbnails,
  setForm,
  setTemplate,
  setPayload,
  addSubmission,
  removeSubmission,
  resetPayload,
  resetState
} = formSlice.actions
